import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Alert,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { Box } from '@mui/material';
import { TextField, Grid, TextareaAutosize } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { WotcSurveyData } from './types';
import { authContext } from '@staffedupusa/shared-components';
import dayjs from 'dayjs';
import { BasicInformationIcon } from '../../vectors/index';
import { useUserCountry } from '@context/UserCountryContext';
import { useUpdatePersonalProfile } from '@features/profile/hooks/useUpdatePersonalProfile';

type GeneralInfoModalProps = {
  generalInfoModal: boolean;
  wotcSurveyData: WotcSurveyData;
  handleClose: () => void;
  handleNext: () => void;
};
const defaultValues = {
  bdi_first_name: '',
  bdi_middle_name: '',
  bdi_last_name: '',
  bdi_dob: null,
  bdi_street_address1: '',
  bdi_county: '',
  bdi_state: '',
  bdi_city: '',
  bdi_zip_code: '',
  bdi_email: '',
  bdi_phone: '',
};

const GeneralInfoModal: FC<GeneralInfoModalProps> = ({
  generalInfoModal,
  handleClose,
  handleNext,
  wotcSurveyData,
}) => {
  const { userCountry } = useUserCountry();
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });
  const { updatePersonalProfile } = useUpdatePersonalProfile({
    setErrorText,
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues,
  });
  // Function to handle form submission
  const onSubmit = (data: any) => {
    const day = data.bdi_dob.date();
    const month = data.bdi_dob.month() + 1;
    const year = data.bdi_dob.year();
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (!authData?.user?.birthday) {
          updatePersonalProfile({
            birthdayDay: String(day),
            birthdayMonth: String(month),
            birthdayYear: String(year),
          });
        }
        toast.success('Data submitted successfully');
        handleNext();
        handleClose();
      },
    };

    const transformedData = {
      ...data,
      bdi_dob: dayjs(data.bdi_dob).format('YYYY-MM-DD'),
      status: 9,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };
  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (
            typedKey === 'bdi_first_name' &&
            wotcSurveyData[typedKey] == null
          ) {
            setValue(
              typedKey,
              authData?.user?.first_name ||
                authData?.user?.name?.split(' ')?.[0] ||
                '',
            );
          }
          if (
            typedKey === 'bdi_middle_name' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.middle_name
          ) {
            setValue(typedKey, authData?.user?.middle_name);
          }
          if (
            typedKey === 'bdi_last_name' &&
            wotcSurveyData[typedKey] == null &&
            (authData?.user?.last_name || authData?.user?.name?.split(' ')?.[1])
          ) {
            setValue(
              typedKey,
              authData?.user?.last_name ||
                authData?.user?.name?.split(' ')?.[1] ||
                '',
            );
          }
          if (
            typedKey === 'bdi_street_address1' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.address1
          ) {
            setValue(typedKey, authData?.user?.address1);
          }
          if (
            typedKey === 'bdi_email' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.email
          ) {
            setValue(typedKey, authData?.user?.email);
          }
          if (
            typedKey === 'bdi_phone' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.phone
          ) {
            setValue(typedKey, authData?.user?.phone);
          }
          if (
            typedKey === 'bdi_zip_code' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.zip
          ) {
            setValue(typedKey, authData?.user?.zip);
          }
          if (
            typedKey === 'bdi_city' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.city
          ) {
            setValue(typedKey, authData?.user?.city);
          }
          if (
            typedKey === 'bdi_state' &&
            wotcSurveyData[typedKey] == null &&
            authData?.user?.state
          ) {
            setValue(typedKey, authData?.user?.state);
          }
          if (typedKey === 'bdi_dob') {
            const dateValue =
              wotcSurveyData[typedKey] || authData?.user?.birthday || null;
            const date = dateValue ? dayjs(dateValue as string) : null;
            setValue(typedKey, date);
          } else if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, authData, setValue]);

  return (
    <Dialog
      open={generalInfoModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="10px"
        >
          <BasicInformationIcon />
          <Typography variant="h5">Applicant Information</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mt={3}>
          {errorText && (
            <Alert sx={{ mb: 3 }} severity="error">
              {errorText}
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4}>
              {/* First Name */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_first_name"
                  control={control}
                  rules={{ required: 'First Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      size="small"
                      fullWidth
                      error={!!errors.bdi_first_name}
                      helperText={errors.bdi_first_name?.message}
                    />
                  )}
                />
              </Grid>

              {/* Middle Name */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_middle_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Middle Name"
                      fullWidth
                      error={!!errors.bdi_middle_name}
                      helperText={errors.bdi_middle_name?.message}
                    />
                  )}
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_last_name"
                  control={control}
                  rules={{ required: 'Last Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      size="small"
                      error={!!errors.bdi_last_name}
                      helperText={errors.bdi_last_name?.message}
                    />
                  )}
                />
              </Grid>

              {/* Birthdate */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_dob"
                  control={control}
                  rules={{ required: 'Birth Date is required' }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      disableFuture
                      label="Birth Date"
                      sx={{
                        width: '100%',
                        '.MuiInputLabel-root': {
                          top: '-6px',
                          fontSize: '0.875rem',
                        },
                        '.MuiInputBase-root': {
                          height: 40,
                        },
                        '.MuiInputBase-input': {
                          padding: '10px 12px',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  )}
                />
                {errors.bdi_dob && (
                  <span
                    style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                  >
                    {errors.bdi_dob?.message}
                  </span>
                )}
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Controller
                  name="bdi_street_address1"
                  control={control}
                  rules={{ required: 'Address is required' }}
                  render={({ field }) => (
                    <TextareaAutosize
                      {...field}
                      minRows={3}
                      placeholder="Address"
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: errors.bdi_street_address1
                          ? '1px solid red'
                          : '1px solid #ccc',
                      }}
                    />
                  )}
                />
              </Grid>

              {/* County */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_county"
                  control={control}
                  rules={{ required: 'County is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="County"
                      fullWidth
                      size="small"
                      error={!!errors.bdi_county}
                      helperText={errors.bdi_county?.message}
                    />
                  )}
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_state"
                  control={control}
                  rules={{ required: 'State is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="State"
                      size="small"
                      fullWidth
                      error={!!errors.bdi_state}
                      helperText={errors.bdi_state?.message}
                    />
                  )}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_city"
                  control={control}
                  rules={{ required: 'City is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="City"
                      size="small"
                      fullWidth
                      error={!!errors.bdi_city}
                      helperText={errors.bdi_city?.message}
                    />
                  )}
                />
              </Grid>

              {/* Zipcode */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_zip_code"
                  control={control}
                  rules={{
                    required: 'Zipcode is required',
                    pattern: {
                      value: userCountry === 'au' ? /^[0-9]{4}$/ : /^[0-9]{5}$/,
                      message:
                        userCountry === 'au'
                          ? 'Zipcode must be 4 digits'
                          : 'Zipcode must be 5 digits',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Zipcode"
                      size="small"
                      fullWidth
                      error={!!errors.bdi_zip_code}
                      helperText={errors.bdi_zip_code?.message}
                    />
                  )}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      size="small"
                      fullWidth
                      error={!!errors.bdi_email}
                      helperText={errors.bdi_email?.message}
                    />
                  )}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bdi_phone"
                  control={control}
                  rules={{
                    required: 'Phone number is required',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone Number"
                      fullWidth
                      size="small"
                      error={!!errors.bdi_phone}
                      helperText={errors.bdi_phone?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {/* Submit Button */}
            <Box width="100%" textAlign="center">
              <LoadingButton
                loading={isSendingWotcSurvey}
                sx={{ width: '100%', mt: 3 }}
                type="submit"
                variant="contained"
                color="primary"
              >
                Next
              </LoadingButton>
            </Box>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GeneralInfoModal;
